import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import T from "../components/T";
import { ChevronLeft, ChevronRight, Check, Clock } from "lucide-react";
import { listLessons, CATEGORIES, categoryLabel } from "../data/lessons";
import { listCompletedLessons } from "../lib/localProgress";

export default function Lessons() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [activeCat, setActiveCat] = useState("all");
  const [done, setDone] = useState([]);

  useEffect(() => { setDone(listCompletedLessons()); }, []);

  const lessons = listLessons().filter((l) => activeCat === "all" || l.category === activeCat);

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-lessons">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2">
          <ChevronLeft className="w-4 h-4" /> {t("v11.back")}
        </button>
        <p className="font-script text-2xl text-brand">{t("v11.lessons.kicker")}</p>
        <h1 className="font-serif text-4xl leading-tight"><T i18nKey="v11.lessons.h" /></h1>
        <p className="mt-3 text-foreground/70 leading-relaxed">{t("v11.lessons.intro")}</p>
      </div>

      <div className="px-8 mb-4 flex flex-wrap gap-2" data-testid="lesson-filters">
        {CATEGORIES.map((c) => (
          <button
            key={c.slug}
            data-testid={`cat-${c.slug}`}
            onClick={() => setActiveCat(c.slug)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
              activeCat === c.slug
                ? "bg-brand text-white border-brand"
                : "bg-card border-border text-foreground/70 hover:border-brand/50"
            }`}
          >
            {categoryLabel(c.slug, lang)}
          </button>
        ))}
      </div>

      <div className="px-8 space-y-3 pb-10">
        {lessons.length === 0 && (
          <div className="rounded-3xl bg-card border border-dashed border-border p-6 text-center text-foreground/65" data-testid="lessons-empty">
            {t("v11.lessons.empty_category")}
          </div>
        )}
        {lessons.map((l) => {
          const isDone = done.includes(l.slug);
          return (
            <button
              key={l.slug}
              data-testid={`lesson-row-${l.slug}`}
              onClick={() => navigate(`/lessons/${l.slug}`)}
              className="w-full text-left rounded-3xl bg-card border border-border p-5 hover:border-brand/50 transition-all flex items-start gap-3"
            >
              <span className={`mt-0.5 w-6 h-6 rounded-full grid place-items-center shrink-0 border ${
                isDone ? "bg-brand border-brand text-white" : "border-foreground/25"
              }`}>
                {isDone && <Check className="w-3.5 h-3.5" />}
              </span>
              <div className="flex-1">
                <div className="font-serif text-lg">{lang === "es" ? l.title_es : l.title_en}</div>
                <div className="mt-1 flex items-center gap-3 text-xs text-foreground/55">
                  <span>{categoryLabel(l.category, lang)}</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {t("v11.lessons.minutes", { n: l.read_minutes })}
                  </span>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-foreground/40 mt-2" />
            </button>
          );
        })}
      </div>
    </AppShell>
  );
}
